import { useEffect } from 'react';

import { Money, formatMoney } from '@betterment/core';
import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';
import { StepProps, useAutoNavigate, useFlowState } from '@betterment/js-runtime';
import { Banner, Box, Button, Dialog, H1, Icon, Separator, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { ErrorBoundary, useFeatureFlag } from '@betterment/shared-components';
import {
  moneyFromSharesTradeEstimate,
  moneyTradeEstimateCopy,
  sharesFromMoneyTradeEstimate,
  sharesTradeEstimateCopy,
} from '@betterment/shared-components/src/utils/tradeEstimateHelpers';
import { Form, Formik, FormikProps } from 'formik';
import { useNavigate } from 'react-router-dom';

import { OrderUnitType, SaleScope, SellOrderProps, ValidatedOrderValues, Validations } from './types';

import { CreateFinancialSecurityOrderRequestDocument } from '../../buy-order-flow/graphql/mutation.createFinancialSecurityOrderRequest.generated';
import { EstimatedTradeTimingDialog } from '../../buy-order-flow/steps/EstimatedTradeTimingDialog';
import { TaxImpactUnavailable } from '../../direct-trading-accounts/shared/TaxImpactUnavailable';
import { SELL_ORDER_COPY } from '../../direct-trading-accounts/shared/TaxImpactUnavailableCopy';
import { trackClick } from '../../features/track';
import { FinancialSecurityOrderRequestDirection, TaxImpact as GraphQLTaxImpact } from '../../graphql/schema';
import { TaxImpact } from '../../Shared/data';
import { WorkflowLayout } from '../../Shared/layout/WorkflowLayout';
import { TaxImpactDialogContent, formatEstimatedTaxes } from '../../Shared/TaxImpact';
import { DirectTradingAccountSellSecurityPositionsDocument } from '../graphql/directTradingAccountSellSecurityPositions.generated';

export type ReviewStepProps = SellOrderProps & StepProps;

const convertGraphQLTaxImpactToTaxImpact = (graphqlTaxImpact: GraphQLTaxImpact): TaxImpact => {
  return {
    transactionPending: graphqlTaxImpact.hasTransactionPending,
    longTermGainCents: graphqlTaxImpact.longTermGain.cents,
    longTermLossCents: graphqlTaxImpact.longTermLoss.cents,
    shortTermGainCents: graphqlTaxImpact.shortTermGain.cents,
    shortTermLossCents: graphqlTaxImpact.shortTermLoss.cents,
    longTermWashedCents: graphqlTaxImpact.longTermWashed.cents,
    longTermPermanentlyWashedCents: graphqlTaxImpact.longTermPermanentlyWashed.cents,
    shortTermWashedCents: graphqlTaxImpact.shortTermWashed.cents,
    shortTermPermanentlyWashedCents: graphqlTaxImpact.shortTermPermanentlyWashed.cents,
    netLongTermCents: graphqlTaxImpact.netLongTerm.cents,
    netLongTermBeforeWashCents: graphqlTaxImpact.netLongTermBeforeWash.cents,
    netShortTermCents: graphqlTaxImpact.netShortTerm.cents,
    netShortTermBeforeWashCents: graphqlTaxImpact.netShortTermBeforeWash.cents,
    netGainCents: graphqlTaxImpact.netGain.cents,
    netWashedLossesCents: graphqlTaxImpact.netWashedLosses.cents,
    netEstimatedTaxesCents: graphqlTaxImpact.netEstimatedTaxes.cents,
    lossWithWashed: graphqlTaxImpact.isLossWithWashed,
    gain: graphqlTaxImpact.isGain,
    howWeEstimateTaxImpactCopy: graphqlTaxImpact.copy.howWeEstimateTaxImpact,
    longTermGainEstimationCopy: graphqlTaxImpact.copy.longTermGainEstimation,
    pendingCopy: graphqlTaxImpact.copy.pendingExplanation,
    washCopy: graphqlTaxImpact.copy.washSaleExplanation,
    shortTermGainEstimationCopy: graphqlTaxImpact.copy.shortTermGainEstimation,
    taxMinExplanationCopy: graphqlTaxImpact.copy.taxMinExplanation,
    hasNonCoveredTaxLots: graphqlTaxImpact.hasNonCoveredTaxLots,
    mayImpactGainsAllowance: graphqlTaxImpact.mayImpactGainsAllowance,
    estimatedTaxImpactBannerCopy: graphqlTaxImpact.copy.estimatedTaxImpactBanner,
    estimatedTaxImpactDisclosureCopy: graphqlTaxImpact.copy.estimatedTaxImpactDisclosure,
  };
};

const ShortTermGainsBanner: React.FC = () => (
  <Banner variant="info">
    <Banner.Content>
      <Text>At the estimated price, this sale will result in short-term capital gains.</Text>
      <Dialog>
        <Dialog.Trigger asChild={true}>
          <Button type="button" variant="textPrimary" aria-label="Learn more">
            Learn more
          </Button>
        </Dialog.Trigger>
        <Dialog.Content css={{ p: 'space.400', width: '50%' }}>
          <Dialog.Title>
            <H1>How to avoid short-term capital gains</H1>
          </Dialog.Title>
          <Dialog.Description>
            Selling now means paying a higher tax rate on your gains. If you hold a security for over a year from when
            you bought it, you’ll qualify for lower long-term capital gains rates and keep more of your money.
          </Dialog.Description>
          <Dialog.Close asChild={true}>
            <Button variant="solidSecondary" css={{ mt: 'space.300' }}>
              Close
            </Button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog>
    </Banner.Content>
  </Banner>
);

export const ReviewStep: React.FC<ReviewStepProps> = ({ accountId, securityId, goToNextStep, goToStep }) => {
  const { data } = useGraphQLQuery(DirectTradingAccountSellSecurityPositionsDocument, {
    variables: { accountId },
    throwOnError: true,
  });

  const { flowState } = useFlowState<ValidatedOrderValues & Validations>();
  const autoNavigate = useAutoNavigate();
  const navigate = useNavigate();
  const { enabled: isAfterHoursCancellationEnabled } = useFeatureFlag({
    splitName: 'retail.self_directed_after_hours_order_cancellation_q4_2025_enabled',
    context: 'sell_order_review_step',
  });

  const [createFinancialSecurityOrderRequest, { loading: isMutationLoading }] = useGraphQLMutation(
    CreateFinancialSecurityOrderRequestDocument
  );

  const inconsistentUnitAndValues = (values: ValidatedOrderValues) => {
    if (!values.order_unit) {
      return true;
    }

    if (values.order_unit === OrderUnitType.Dollars && !values.order_dollar_amount_cents) {
      return true;
    }

    if (values.order_unit === OrderUnitType.Shares && !values.order_shares_amount) {
      return true;
    }

    return false;
  };

  const estimatedTradeTiming = flowState.values.estimated_trade_timing;
  const taxImpactPreview = flowState.values.tax_impact_preview;
  const willOrderBeCancelable = flowState.values.order_will_be_cancelable;

  useEffect(() => {
    if (inconsistentUnitAndValues(flowState.values)) {
      goToStep('form');
    }
  }, [flowState.values, goToStep]);

  if (!data) return null;

  const { account } = data;
  const securityPosition = account.securityPositions.find(sp => sp.financialSecurity.id === securityId);

  if (!securityPosition) return null;

  const { financialSecurity } = securityPosition;
  const price = financialSecurity.statistics.latestPrice.amount;

  const taxImpact = taxImpactPreview ? convertGraphQLTaxImpactToTaxImpact(taxImpactPreview) : null;

  const handleSubmit = async (values: ValidatedOrderValues) => {
    if (inconsistentUnitAndValues(values)) {
      return;
    }

    const value = () => {
      if (values.order_dollar_amount_cents) {
        return { amount: Money(values.order_dollar_amount_cents) };
      }
      return { shares: Number(values.order_shares_amount) };
    };

    try {
      await createFinancialSecurityOrderRequest({
        variables: {
          input: {
            accountId: { directTradingAccountId: data.account.id },
            financialSecurityId: financialSecurity.id,
            direction: FinancialSecurityOrderRequestDirection.Sell,
            value: value(),
            sellAll: values.order_type === SaleScope.SellAll,
            estimatedShares: sharesFromMoneyTradeEstimate(
              values.order_dollar_amount_cents ? Money(values.order_dollar_amount_cents) : undefined,
              price
            ),
            estimatedMoneyAmount: moneyFromSharesTradeEstimate(values.order_shares_amount, price),
            sharePriceAtRequest: price,
          },
        },
      });

      goToNextStep();
    } catch (error) {
      void navigate('/generic_error');
    }
  };

  const formattedAmountCopy = ({ values }: FormikProps<ValidatedOrderValues>) => {
    if (values.order_unit === OrderUnitType.Dollars) {
      return values.order_dollar_amount_cents && formatMoney(Money(values.order_dollar_amount_cents));
    }
    const shareString = `${values.order_shares_amount} share${values.order_shares_amount === 1 ? '' : 's'}`;
    if (values.order_type === SaleScope.SellAll) {
      return `All ${shareString}`;
    }
    return shareString;
  };

  const estimatedCopy = ({ values }: FormikProps<ValidatedOrderValues>) => {
    if (values.order_unit === OrderUnitType.Dollars) {
      return sharesTradeEstimateCopy(
        values.order_dollar_amount_cents ? Money(values.order_dollar_amount_cents) : undefined,
        price
      );
    }

    return moneyTradeEstimateCopy(values.order_shares_amount, price);
  };

  const initialValues: ValidatedOrderValues = {
    order_dollar_amount_cents: flowState.values.order_dollar_amount_cents,
    order_shares_amount: flowState.values.order_shares_amount,
    order_unit: flowState.values.order_unit,
    order_type: flowState.values.order_type,
  };

  return (
    <ErrorBoundary>
      <WorkflowLayout
        pageTitle="Sell"
        workflowTitle="Sell"
        size="medium"
        onBack={() =>
          goToStep('form', {
            order_dollar_amount_cents: flowState.values.order_dollar_amount_cents,
            order_shares_amount: flowState.values.order_shares_amount,
            order_unit: flowState.values.order_unit,
            order_type: flowState.values.order_type,
          })
        }
        onClose={() => autoNavigate(flowState.returnToPath)}
      >
        <H1 css={{ marginBottom: 'space.200' }}>Review {financialSecurity.symbol} sell</H1>
        {!isAfterHoursCancellationEnabled && (
          <Text css={{ marginBottom: 'space.300' }}>Submitted orders can't be canceled or edited.</Text>
        )}
        <Formik onSubmit={handleSubmit} initialValues={initialValues}>
          {(formikProps: FormikProps<ValidatedOrderValues>) => (
            <Form className={css({ width: '100%' })}>
              <Box
                css={{
                  width: '100%',
                  borderRadius: 'medium',
                  border: 'token(borderWidths.thin) solid token(colors.border)',
                  padding: 'space.300',
                }}
              >
                <Box
                  css={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box css={{ alignSelf: 'center' }}>
                    <Text>Security</Text>
                  </Box>
                  <Box css={{ display: 'flex', flexDirection: 'column', textAlign: 'end' }}>
                    <Text css={{ alignSelf: 'end' }}>{financialSecurity.name}</Text>
                    <Text css={{ alignSelf: 'end', color: 'text.weak' }} variant="text50">
                      {financialSecurity.symbol}
                    </Text>
                  </Box>
                </Box>
                <Separator
                  css={{
                    marginY: 'space.300',
                  }}
                />
                <Box
                  css={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box css={{ alignSelf: 'center' }}>
                    <Text>Amount</Text>
                  </Box>
                  <Box css={{ display: 'flex', flexDirection: 'column', textAlign: 'end' }}>
                    <Text css={{ alignSelf: 'end' }}>{formattedAmountCopy(formikProps)}</Text>
                    <Text css={{ alignSelf: 'end', color: 'text.weak' }} variant="text50">
                      {estimatedCopy(formikProps)}
                    </Text>
                  </Box>
                </Box>
                {taxImpact && (
                  <>
                    <Separator
                      css={{
                        marginY: 'space.300',
                      }}
                    />
                    <Dialog>
                      <Dialog.Trigger asChild={true}>
                        <Box
                          onClick={() => trackClick({ name: 'TaxImpactPreview' })}
                          css={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                          }}
                        >
                          <Box css={{ alignSelf: 'center' }}>
                            <Text>Estimated taxes {taxImpact.netEstimatedTaxesCents < 0 ? 'saved' : 'owed'}</Text>
                          </Box>
                          <Box
                            css={{
                              display: 'flex',
                              flexDirection: 'row',
                              textAlign: 'right',
                              gap: 'space.100',
                              alignItems: 'center',
                            }}
                          >
                            <Text css={{ alignSelf: 'end' }}>{formatEstimatedTaxes(taxImpact)}</Text>
                            <Icon size="small" name="CaretRight" />
                          </Box>
                        </Box>
                      </Dialog.Trigger>
                      <TaxImpactDialogContent {...taxImpact} />
                    </Dialog>
                  </>
                )}
                {!taxImpact && (
                  <>
                    <Separator
                      css={{
                        marginY: 'space.300',
                      }}
                    />
                    <TaxImpactUnavailable header={SELL_ORDER_COPY.header} body={SELL_ORDER_COPY.body} />
                  </>
                )}
                {estimatedTradeTiming && (
                  <>
                    <Separator
                      css={{
                        marginY: 'space.300',
                      }}
                    />
                    <EstimatedTradeTimingDialog estimatedTradeTiming={estimatedTradeTiming} />
                  </>
                )}
                {taxImpact && taxImpact.netShortTermCents > 0 && (
                  <>
                    <Separator
                      css={{
                        marginY: 'space.300',
                      }}
                    />
                    <ShortTermGainsBanner />
                  </>
                )}
              </Box>
              {isAfterHoursCancellationEnabled && willOrderBeCancelable && (
                <Box css={{ marginTop: 'space.200' }}>
                  <Text variant="text50" color="text.weak">
                    Orders can be canceled outside of market hours.
                  </Text>
                </Box>
              )}
              <Button
                css={{
                  marginTop: 'space.300',
                  px: 'space.600',
                }}
                type="submit"
                variant="solidPrimary"
                loading={isMutationLoading}
              >
                Submit
              </Button>
            </Form>
          )}
        </Formik>
      </WorkflowLayout>
    </ErrorBoundary>
  );
};
